import { materialBarvy } from '../../lib/uiTokens';

/**
 * Sloupcová mřížka položek dokladu — protějšek `spizirna/materialGrid.js`
 * a drží stejná pravidla (standard-rozvrzeni.md): šířky na jednom místě pro
 * hlavičku, řádky i skupiny; volnou šířku bere název; číselné sloupce mají
 * pevnou šířku a sedí vpravo jako blok.
 *
 * Co se u položky hlídá, je jediné: jde s ní zaúčtovat doklad. Sloupce proto
 * nesou typ, P&L účet a částku — množství a cena za jednotku jdou do meta
 * řádky pod název, stejně jako u suroviny. Dřív byla každá položka tučná
 * karta se šesti trvale vykreslenými dropdowny: na 760px sloupec se vešly
 * tři řádky a nešlo přejet očima po jednom sloupci.
 */
export const GRID_POL = {
    vyberka: 26,    // zaškrtávátko pro hromadné zařazení
    semafor: 4,     // barevný pásek stavu řádku
    nazevMin: 180,  // název + meta — pod tohle nesmí spadnout
    // Strop názvu. Bez něj bral sloupec všechno volné místo a zařazení
    // odtékalo k pravé hraně — mezi názvem a účtem zůstala půl obrazovky
    // prázdna a člověk musel očima přejíždět přes nič.
    nazevMax: 440,
    // Typ a P&L stály jako dva úzké sloupce a stejně se v nich text ořezával.
    // Teď je to jeden sloupec „Zařazení“ o třech řádcích: P&L · typ · položka
    // z katalogu — tři věci, které se u položky vybírají, pohromadě a celé.
    zarazeni: 236,
    celkem: 104,  // částka větším písmem a pod ní sazba DPH
    mezera: 9,
};

/** Typy položek: popisek a barva skupiny. Barvy jsou ze Spižírny, ne nové. */
export function typySkupin(isDark) {
    const mat = materialBarvy(isDark);
    return {
        surovina:   { label: 'Suroviny',        barva: mat.suroviny },
        obal:       { label: 'Obaly',           barva: mat.obaly },
        spotreba:   { label: 'Spotřební zboží', barva: mat.zbozi },
        phm:        { label: 'PHM',             barva: '#f97316' },
        energie:    { label: 'Energie',         barva: '#eab308' },
        sluzba:     { label: 'Služby',          barva: '#8b5cf6' },
        investice:  { label: 'Investice',       barva: '#0cc0df' },
        ostatni:    { label: 'Ostatní',         barva: '#94a3b8' },
    };
}

/** U kterých typů musí být položka napojená na katalog (a pod jakým FK). */
export const TYP_KATALOG_FK = {
    surovina: 'surovina_id',
    obal: 'obal_id',
    spotreba: 'spotrebni_zbozi_id',
    phm: 'tankovani_id',
};

/**
 * Semafor řádku — obdoba `validace` u suroviny, jen s pravidly, která platí
 * pro položku dokladu. Tatáž pravidla hlídá `DokladWorkflowService::
 * validateProSchvaleni`, takže co tu svítí červeně, opravdu brání schválení.
 *
 * `red` = chybí typ (bez něj nejde nic dalšího) nebo vazba do katalogu,
 * `orange` = chybí P&L účet, `null` = položka je v pořádku.
 */
export function stavPolozky(p) {
    const chybi = [];

    if (!p.typ_polozky) {
        chybi.push({ kod: 'typ', text: 'chybí typ', stav: 'red' });
    } else {
        const fk = TYP_KATALOG_FK[p.typ_polozky];
        // PHM se páruje až při zápisu tankování — chybějící vazba u něj není chyba.
        if (fk && p.typ_polozky !== 'phm' && !p[fk]) {
            chybi.push({ kod: 'katalog', text: 'není v katalogu', stav: 'red' });
        }
    }
    if (!p.kategorie_id) {
        chybi.push({ kod: 'ucet', text: 'chybí P&L účet', stav: 'orange' });
    }

    const stav = chybi.some(c => c.stav === 'red') ? 'red'
        : chybi.length ? 'orange'
        : null;

    return { stav, chybi };
}

/** Kolik položek má kterou závadu — pro pruh „co brání schválení". */
export function pocetZavad(polozky = []) {
    let bezTypu = 0, bezUctu = 0, bezKatalogu = 0;
    for (const p of polozky) {
        const { chybi } = stavPolozky(p);
        for (const c of chybi) {
            if (c.kod === 'typ') bezTypu++;
            if (c.kod === 'ucet') bezUctu++;
            if (c.kod === 'katalog') bezKatalogu++;
        }
    }
    return { bezTypu, bezUctu, bezKatalogu };
}

/**
 * Položky do skupin podle typu, se součtem za skupinu — vzor jsou kategorie
 * ve stromu surovin, které nesou souhrn za podstrom.
 *
 * Skupina „Bez typu" jde vždy NAHORU: je to jediná věc na téhle záložce, která
 * brání schválení, takže se k ní nemá scrollovat. Zbytek jde v pořadí typů
 * tak, jak je vyjmenovaný v `typySkupin`.
 */
export function seskupPolozky(polozky = [], isDark = false) {
    const typy = typySkupin(isDark);
    const mapa = new Map();

    for (const p of polozky) {
        const klic = p.typ_polozky || '__bez__';
        if (!mapa.has(klic)) mapa.set(klic, []);
        mapa.get(klic).push(p);
    }

    const poradi = Object.keys(typy);
    const skupiny = [];

    const postav = (klic, polozkyVe) => {
        const meta = typy[klic];
        const soucet = polozkyVe.reduce((s, p) => s + parseFloat(p.cena_celkem_s_dph || 0), 0);
        // Názvy účtů, ne kódy — `FC_SUROVINY` nikomu nic neřekne.
        const ucty = [...new Set(polozkyVe.map(p => p.kategorie?.nazev).filter(Boolean))];
        const problemu = polozkyVe.filter(p => stavPolozky(p).stav === 'red').length;
        return {
            klic,
            label: meta?.label ?? 'Bez typu',
            barva: meta?.barva ?? '#ef4444',
            blokuje: !meta,          // bez typu = brání schválení
            polozky: polozkyVe,
            soucet,
            ucty,
            problemu,
        };
    };

    if (mapa.has('__bez__')) skupiny.push(postav('__bez__', mapa.get('__bez__')));
    for (const klic of poradi) {
        if (mapa.has(klic)) skupiny.push(postav(klic, mapa.get(klic)));
    }

    return skupiny;
}

/**
 * P&L účty volitelné u položky dokladu — Přímé náklady (Suroviny/Zboží/Obaly),
 * Ostatní náklady (subúčty A–H) a Energie (vyúčtování plyn/elektřina/voda chodí
 * přes doklad, takže musí jít vybrat). Personál, zmetkovitost a pokladní manka
 * přes doklady nechodí. Rodičovské účty volitelné nejsou — jen listy.
 */
const POVOLENE_SKUPINY = ['prime_naklady', 'ostatni_naklady', 'energie'];

/**
 * Energie nemají rodičovský účet a jmenují se podle média („Plyn (odečet + …)“),
 * takže v seznamu zapadly a hledání „energie“ nic nenašlo. Prefix skupiny je
 * dává k sobě stejně jako „Nájem a provozovna › …“ u subúčtů A–H.
 */
const PREFIX_SKUPINY = { energie: 'Energie' };

export function leafKategorie(kategorie = []) {
    const nazevById = Object.fromEntries(kategorie.map(k => [k.id, k.nazev]));
    const jeRodic = new Set(kategorie.filter(k => k.parent_id).map(k => k.parent_id));
    return kategorie
        .filter(k => !jeRodic.has(k.id) && POVOLENE_SKUPINY.includes(k.skupina))
        .map(k => {
            const prefix = k.parent_id ? nazevById[k.parent_id] || '' : PREFIX_SKUPINY[k.skupina];
            return { id: k.id, label: prefix ? `${prefix} › ${k.nazev}` : k.nazev };
        });
}
